import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, Renderer2 } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BaseFormComponent } from 'src/app/core/abstractions/base-form.component';
import { CustomValidators } from 'src/app/core/utils/custom-validators';
import * as gtag from 'src/app/core/utils/gtag';
import { environment } from 'src/environments/environment';

import { LINKEDIN_CONVERSION_ID_INTERESSSIERT_FORM_CONFIRMED } from 'src/app/app.constants';
import { CalculatorService } from 'src/app/shared/components/calculator/calculator.service';

declare let fbq: Function;

@UntilDestroy()
@Component({
    selector: 'app-home-interested-form',
    templateUrl: './home-interested-form.component.html',
    styleUrls: ['./home-interested-form.component.scss']
})
export class HomeInterestedFormComponent extends BaseFormComponent implements OnInit, AfterViewInit {
    currentFocusedFormInput = 0;
    formEnabled = false;
    mailSent = false;
    interestedFormContainer: Element;
    buttonHereWeGo: Element;
    buttonBookDemo: Element;
    companyRoles = [
        {
            id: 'DIRECTOR',
            value: 'company-roles.director'
        },
        {
            id: 'HUMAN_RESOURCES',
            value: 'company-roles.human-resources'
        },
        {
            id: 'PAYROLL_BOOKKEEPING',
            value: 'company-roles.payroll-bookkeeping'
        },
        {
            id: 'OTHER',
            value: 'company-roles.other'
        }
    ];
    formMobileScrollPosition = 500;
    formMobilePosition: 'fixed' | 'sticky';

    isMobile: boolean;

    @Input() isFaqLoginPage = false;
    @Output() formEnableEvent: EventEmitter<boolean> = new EventEmitter<boolean>();

    @HostListener('window:scroll', ['$event'])
    isScrolledIntoView() {
        if (!this.isMobile && this.formMobilePosition !== 'sticky' &&
        window.scrollY > 0 && window.scrollY < this.formMobileScrollPosition &&
        !this.formEnabled && !this.isFaqLoginPage
        ) {
            this.renderer.addClass(this.interestedFormContainer, 'fade-out');
            this.formMobilePosition = 'sticky';
        }

        if (this.isMobile && !this.formEnabled) {
            if (window.scrollY === 0 && !document.getElementsByClassName('cookies-dialog')?.length) {
                this.renderer.addClass(this.interestedFormContainer, 'fade-out');
                this.formMobilePosition = 'sticky';
            } else if (window.scrollY > 0 && window.scrollY < this.formMobileScrollPosition) {
                this.renderer.removeClass(this.interestedFormContainer, 'fade-out');
            }
        }
        if (this.formMobilePosition !== 'fixed' && window.scrollY >= this.formMobileScrollPosition) {
            this.renderer.removeClass(this.interestedFormContainer, 'fade-out');
            this.renderer.setStyle(this.interestedFormContainer, 'display', 'initial');
            this.renderer.setStyle(this.interestedFormContainer, 'position', 'fixed');
            if (this.isMobile) {
                this.renderer.setStyle(this.interestedFormContainer, 'top', '0');
                this.renderer.addClass(this.interestedFormContainer, 'slide-in-bottom');
            } else {
                this.renderer.setStyle(this.interestedFormContainer, 'bottom', '0');
            }
            this.formMobilePosition = 'fixed';
        }
    }

    constructor(
        private calculatorService: CalculatorService,
        private el: ElementRef,
        private renderer: Renderer2,
    ) {
        super();
    }

    ngOnInit() {
        this.isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        super.ngOnInit();
        this.interestedFormContainer = this.el.nativeElement.querySelector('#interestedFormContainer');
    }

    ngAfterViewInit(): void {
        this.buttonHereWeGo = this.el.nativeElement.querySelector('#buttonHereWeGo');
        this.buttonBookDemo = this.el.nativeElement.querySelector('#buttonBookDemo');

        // loads hubspot js lib
        this.loadHubspotMeetingModal();
    }

    shouldOpenInterestedForm() {
        this.enableOrDisableForm();
    }

    enableOrDisableForm() {
        this.formEnabled = !this.formEnabled;
        this.formEnableEvent.emit(this.formEnabled);
        if (this.formMobilePosition === 'fixed' && !this.formEnabled) {
            this.renderer.setStyle(this.interestedFormContainer, 'position', 'fixed');
            this.formMobilePosition = 'fixed';
        }
        if (this.formEnabled) {
            this.renderer.addClass(this.interestedFormContainer, 'showForm');
            this.renderer.setStyle(this.interestedFormContainer, 'position', 'fixed');
            if (environment.production) {
                gtag.event({
                    action: 'interested-form-opened',
                    category: 'main_functions',
                    label: 'The "Interested?" form was opened',
                    value: 1
                });
                fbq('track', '"Interested?" form opened');
            }

            if (!this.isMobile) {
                const section = document.querySelectorAll('.interested-form')[0];
                const element = section.getElementsByTagName('input')[0];
                element.focus();
            }
        } else {
            this.renderer.removeClass(this.interestedFormContainer, 'showForm');
            this.isScrolledIntoView();
        }
    }

    isAnyInputFocused() {
        return document.activeElement.hasAttribute('formcontrolname');
    }

    getFormControls() {
        return {
            firstName: new FormControl('', [Validators.required]),
            lastName: new FormControl('', [Validators.required]),
            companyName: new FormControl('', [Validators.required]),
            companyPhone: new FormControl('', [Validators.required, CustomValidators.phone]),
            companyRole: new FormControl(null, []),
            companyEmail: new FormControl('', [Validators.required, CustomValidators.email]),
        };
    }

    onSubmit() {
        this.spinner.show();

        const body = this.form.value;
        if (this.calculatorService.calculatorInput && this.calculatorService.calculatorResult) {
            body.calculatorInput = this.calculatorService.calculatorInput;
            body.calculatorOutput = this.calculatorService.calculatorResult;
        }

        this.calculatorService.createCompanyLead(body)
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                this.spinner.hide();
                this.notification.success('messages.interested-form-sent-succesfully');
                this.enableOrDisableForm();
                if (environment.production) {
                    gtag.event({
                        action: 'interested-form-sent',
                        category: 'main_functions',
                        label: 'The "Interested?" form was sent',
                        value: 1
                    });
                    fbq('track', '"Interested?" form sent');
                    window.lintrk('track', { conversion_id: LINKEDIN_CONVERSION_ID_INTERESSSIERT_FORM_CONFIRMED });
                }
                this.mailSent = true;
                this.renderer.addClass(this.interestedFormContainer, 'mail-sent');
                this.form.reset();
            }, () => {
                this.spinner.hide();
                this.notification.error('messages.interested-form-failed');
            });
    }

    openScheduleEvent() {
        if (environment.production) {
            gtag.event({
                action: 'book-a-demo-clicked-home',
                category: 'main_functions',
                label: 'The Book a Demo button was clicked (Home page)',
                value: 1
            });
            fbq('track', 'Book a Demo clicked (Home page)');
        } else {
            this.notification.error('This functionality is only available in the prod environment');
        }
    }

    loadHubspotMeetingModal() {
        const hubspotScript = document.createElement('script');
        hubspotScript.src = 'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js';
        document.body.appendChild(hubspotScript);

        const iframeContainer = document.getElementsByClassName('meetings-iframe-container')[0];
        iframeContainer.setAttribute('data-src', environment.bookDemoMeetingURL);        
    }
}
